import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tag, Plus, User, LogOut, LayoutDashboard, Heart, MessageSquare, Search, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingPlaceAd, setPendingPlaceAd] = useState(false);

  const handlePlaceAd = () => {
    if (user) navigate("/place-ad");
    else { setPendingPlaceAd(true); setAuthOpen(true); }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-emerald-600 text-white">
            <Tag size={18} strokeWidth={2.5} />
          </span>
          <span className="font-heading font-extrabold text-xl tracking-tight text-slate-900">Deal<span className="text-emerald-600">Shed</span></span>
        </Link>

        <button onClick={() => navigate("/search")} data-testid="header-search-button"
          className="hidden md:flex flex-1 max-w-md items-center gap-2 text-left text-slate-400 border border-slate-300 rounded-md px-4 py-2 hover:border-emerald-500 transition-colors">
          <Search size={16} /> <span className="text-sm">Search DealShed...</span>
        </button>

        <div className="flex items-center gap-2 sm:gap-3">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={() => navigate("/admin")} data-testid="header-admin-link"
              className="font-semibold gap-1.5 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white">
              <ShieldAlert size={16} /> <span className="hidden sm:inline">Admin</span>
            </Button>
          )}
          <Button onClick={handlePlaceAd} data-testid="place-ad-button"
            className="bg-amber-500 hover:bg-amber-600 text-white font-semibold gap-1.5 shadow-sm">
            <Plus size={18} /> <span className="hidden sm:inline">Place Ad</span>
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="user-menu-button" className="rounded-full">
                  <User size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}><LayoutDashboard size={16} className="mr-2" /> My DealShed</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-saved" onClick={() => navigate("/dashboard?tab=saved")}><Heart size={16} className="mr-2" /> Saved Ads</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate("/dashboard?tab=messages")}><MessageSquare size={16} className="mr-2" /> Messages</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><ShieldAlert size={16} className="mr-2" /> Admin panel</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}><LogOut size={16} className="mr-2" /> Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" onClick={() => setAuthOpen(true)} data-testid="sign-in-button" className="font-medium">
              Sign In
            </Button>
          )}
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => { if (pendingPlaceAd) { setPendingPlaceAd(false); navigate("/place-ad"); } }} />
    </header>
  );
}
